import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { InfraAPI } from "@/lib/infra-api";
import { SlideOver, SaveStatus, Section } from "./SlideOver";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, ImageIcon } from "lucide-react";

const TYPE_OPTIONS = ["school", "college", "university", "residential", "day", "boarding", "co-ed", "girls", "boys"];

export function CampusProfilePanel({ open, onClose, campusId }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["infra-profile", campusId],
    queryFn: () => InfraAPI.getProfile(campusId),
    enabled: !!campusId && open,
  });
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("idle");
  const initialised = useRef(false);

  useEffect(() => {
    if (q.data && !initialised.current) {
      setForm(q.data);
      initialised.current = true;
    }
  }, [q.data]);

  useEffect(() => {
    if (!open) initialised.current = false;
  }, [open]);

  // Save-on-blur mutation
  const save = useMutation({
    mutationFn: (patch) => InfraAPI.saveProfile(campusId, patch),
    onMutate: () => setStatus("saving"),
    onSuccess: (data) => {
      setStatus("saved");
      setForm(data);
      qc.invalidateQueries({ queryKey: ["infra-dashboard", campusId] });
      setTimeout(() => setStatus("idle"), 1200);
    },
    onError: () => {
      setStatus("error");
      toast.error("Could not save profile");
    },
  });

  const patchField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const commit = (name, value) => {
    const current = q.data?.[name];
    if (value === current) return;
    save.mutate({ [name]: value });
  };
  const toggleType = (t) => {
    const arr = new Set(form.campus_types || []);
    if (arr.has(t)) arr.delete(t);
    else arr.add(t);
    const next = [...arr];
    setForm((f) => ({ ...f, campus_types: next }));
    save.mutate({ campus_types: next });
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus("saving");
    try {
      const { file_id } = await InfraAPI.upload(file, campusId);
      await save.mutateAsync({ logo_file_id: file_id });
      toast.success("Logo uploaded");
    } catch (err) {
      setStatus("error");
      toast.error("Upload failed");
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Campus Profile"
      subtitle="General information — every downstream module reads from here."
      right={<SaveStatus status={status} />}
      testid="campus-profile"
    >
      <div className="pb-8">
        <Section title="Identity" description="What is this campus called?">
          <Field label="Campus Name *">
            <Input
              value={form.campus_name || ""}
              onChange={(e) => patchField("campus_name", e.target.value)}
              onBlur={(e) => commit("campus_name", e.target.value)}
              placeholder="e.g. St. Peter's School — Kengeri"
              data-testid="cp-name"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Campus Code *">
              <Input
                value={form.campus_code || ""}
                onChange={(e) => patchField("campus_code", e.target.value)}
                onBlur={(e) => commit("campus_code", e.target.value)}
                placeholder="SPK-01"
                data-testid="cp-code"
              />
            </Field>
            <Field label="Established Year">
              <Input
                type="number"
                value={form.established_year || ""}
                onChange={(e) => patchField("established_year", e.target.value ? Number(e.target.value) : null)}
                onBlur={(e) => commit("established_year", e.target.value ? Number(e.target.value) : null)}
                data-testid="cp-year"
              />
            </Field>
          </div>
          <Field label="Campus Types">
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((t) => {
                const active = (form.campus_types || []).includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`cp-type-${t}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Affiliation">
              <Input
                value={form.affiliation || ""}
                onChange={(e) => patchField("affiliation", e.target.value)}
                onBlur={(e) => commit("affiliation", e.target.value)}
                placeholder="CBSE / ICSE / State Board"
                data-testid="cp-affiliation"
              />
            </Field>
            <Field label="Academic Session">
              <Input
                value={form.academic_session || ""}
                onChange={(e) => patchField("academic_session", e.target.value)}
                onBlur={(e) => commit("academic_session", e.target.value)}
                placeholder="2026-27"
              />
            </Field>
          </div>
        </Section>

        <Section title="Contact">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input
                value={form.phone || ""}
                onChange={(e) => patchField("phone", e.target.value)}
                onBlur={(e) => commit("phone", e.target.value)}
                data-testid="cp-phone"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email || ""}
                onChange={(e) => patchField("email", e.target.value)}
                onBlur={(e) => commit("email", e.target.value)}
                data-testid="cp-email"
              />
            </Field>
          </div>
          <Field label="Website">
            <Input
              value={form.website || ""}
              onChange={(e) => patchField("website", e.target.value)}
              onBlur={(e) => commit("website", e.target.value)}
              placeholder="https://…"
            />
          </Field>
        </Section>

        <Section title="Branding" description="How you appear on notices, reports and mobile apps.">
          <Field label="Campus Motto">
            <Input
              value={form.motto || ""}
              onChange={(e) => patchField("motto", e.target.value)}
              onBlur={(e) => commit("motto", e.target.value)}
              placeholder="Excellence. Compassion. Truth."
            />
          </Field>
          <Field label="Campus Description">
            <Textarea
              rows={3}
              value={form.description || ""}
              onChange={(e) => patchField("description", e.target.value)}
              onBlur={(e) => commit("description", e.target.value)}
              placeholder="Tell us about the campus in a paragraph."
            />
          </Field>
          <Field label="Campus Logo">
            <div className="flex items-center gap-3">
              {form.logo_file_id ? (
                <img
                  src={InfraAPI.fileUrl(form.logo_file_id)}
                  alt="logo"
                  className="h-16 w-16 rounded-lg object-cover border border-slate-200"
                />
              ) : (
                <div className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-slate-400" />
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 cursor-pointer">
                <Upload className="h-4 w-4" />
                Upload logo
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="cp-logo-upload" />
              </label>
            </div>
          </Field>
        </Section>

        <Section title="Operations" description="Leadership + operating hours.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Principal">
              <Input
                value={form.principal_name || ""}
                onChange={(e) => patchField("principal_name", e.target.value)}
                onBlur={(e) => commit("principal_name", e.target.value)}
                data-testid="cp-principal"
              />
            </Field>
            <Field label="Campus Head">
              <Input
                value={form.campus_head || ""}
                onChange={(e) => patchField("campus_head", e.target.value)}
                onBlur={(e) => commit("campus_head", e.target.value)}
              />
            </Field>
          </div>
          <Field label="Operating Hours">
            <Input
              value={form.operating_hours || ""}
              onChange={(e) => patchField("operating_hours", e.target.value)}
              onBlur={(e) => commit("operating_hours", e.target.value)}
              placeholder="Mon-Fri 8:00 – 15:30"
            />
          </Field>
        </Section>

        <Section title="Capacity" description="Numbers that power the Campus 360° and Analytics dashboards." defaultOpen={false}>
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Student Capacity" name="student_capacity" form={form} setForm={setForm} commit={commit} />
            <NumberField label="Current Students" name="current_students" form={form} setForm={setForm} commit={commit} />
            <NumberField label="Teacher Count" name="teacher_count" form={form} setForm={setForm} commit={commit} />
            <NumberField label="Non-Teaching Staff" name="non_teaching_staff" form={form} setForm={setForm} commit={commit} />
            <NumberField label="Average Class Strength" name="average_class_strength" form={form} setForm={setForm} commit={commit} />
            <Field label="Student:Teacher Ratio">
              <Input
                value={form.student_teacher_ratio || ""}
                onChange={(e) => patchField("student_teacher_ratio", e.target.value)}
                onBlur={(e) => commit("student_teacher_ratio", e.target.value)}
                placeholder="15:1"
              />
            </Field>
            <NumberField label="Hostel Capacity" name="hostel_capacity" form={form} setForm={setForm} commit={commit} />
            <NumberField label="Transport Capacity" name="transport_capacity" form={form} setForm={setForm} commit={commit} />
          </div>
        </Section>
      </div>
    </SlideOver>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function NumberField({ label, name, form, setForm, commit }) {
  return (
    <Field label={label}>
      <Input
        type="number"
        value={form[name] ?? ""}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value ? Number(e.target.value) : null }))}
        onBlur={(e) => commit(name, e.target.value ? Number(e.target.value) : null)}
        data-testid={`cp-${name.replace(/_/g, "-")}`}
      />
    </Field>
  );
}
